import type { CodeSessionSource } from '@/generated/prisma/enums';

/**
 * Where a session was started; a Slack session keeps its thread's link when Slack gave one, one
 * cubic started when a pull request opened keeps the pull request's, one an app started over
 * MCP keeps the app's name when it registered one, and one started from a pull request page's
 * chat keeps that page's link.
 */
export type CodeSessionOrigin =
  | { source: 'web' }
  | { source: 'slack'; threadUrl: string | null }
  | { source: 'github'; pullRequestUrl: string }
  | { source: 'mcp'; clientName: string | null }
  | { source: 'pr_page'; pageUrl: string };

/**
 * The `CodeSession` columns an origin is saved in: `sourceUrl` is the conversation a session
 * started outside cubic answers in, and `mcpClientName` the app an `mcp` session came from.
 */
type OriginColumns = {
  source: CodeSessionSource;
  sourceUrl: string | null;
  mcpClientName: string | null;
};

export function originColumns(origin: CodeSessionOrigin): OriginColumns {
  switch (origin.source) {
    case 'web':
      return { source: 'web', sourceUrl: null, mcpClientName: null };
    case 'slack':
      return { source: 'slack', sourceUrl: origin.threadUrl, mcpClientName: null };
    case 'github':
      return { source: 'github', sourceUrl: origin.pullRequestUrl, mcpClientName: null };
    case 'mcp':
      return { source: 'mcp', sourceUrl: null, mcpClientName: origin.clientName };
    case 'pr_page':
      return { source: 'pr_page', sourceUrl: origin.pageUrl, mcpClientName: null };
  }
}

/**
 * The origin `originColumns` saved; null for a session cubic owns, which none describes, and for
 * a `github` or `pr_page` row without its link.
 */
export function savedOrigin(row: OriginColumns): CodeSessionOrigin | null {
  switch (row.source) {
    case 'web':
      return { source: 'web' };
    case 'slack':
      return { source: 'slack', threadUrl: row.sourceUrl };
    case 'github':
      return row.sourceUrl === null ? null : { source: 'github', pullRequestUrl: row.sourceUrl };
    case 'mcp':
      // A window still running code from before `mcpClientName` existed saves rows without it to
      // the query cache other windows restore; read those as from an app that registered no name.
      return { source: 'mcp', clientName: row.mcpClientName ?? null };
    case 'pr_page':
      return row.sourceUrl === null ? null : { source: 'pr_page', pageUrl: row.sourceUrl };
    case 'cubic':
      return null;
  }
}
